import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { PrioritySelect } from './PrioritySelect';
import { ProjectSelector } from '../projects/ProjectSelector';
import { TagInput } from './TagInput';
import { TaskPriority } from '../../types/task';
import { useProjects } from '../../contexts/ProjectContext';
import { cn } from '../../utils/cn';
import {
  PlusIcon,
  ArrowLeftIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { ProjectForm } from '../projects/ProjectForm';
import { Task } from '../../types/task';

interface TaskFormProps {
  onSubmit: (task: any) => void;
  onCancel: () => void;
  showBackButton?: boolean;
  initialData?: Task | null;
}

export const TaskForm: React.FC<TaskFormProps> = ({ 
  onSubmit, 
  onCancel, 
  showBackButton = true,
  initialData = null 
}) => {
  const { projects } = useProjects();
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [newTask, setNewTask] = useState({
    title: initialData?.title || '',
    notes: initialData?.notes || '',
    priority: initialData?.priority,
    estimatedPomodoros: initialData?.estimatedPomodoros || 1,
    tags: initialData?.tags || [],
    recurring: initialData?.recurring,
    projectId: initialData?.projectId || null,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTask.title.trim()) return;
    
    const taskData = {
      ...newTask,
      title: newTask.title.trim(), // Trim whitespace from title
      completedPomodoros: initialData?.completedPomodoros || 0,
      completed: initialData?.completed || false,
      createdAt: initialData?.createdAt || Date.now(),
    };
    
    onSubmit(taskData);
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6">
        {showBackButton && (
          <div className="flex items-center gap-2 mb-6">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onCancel}
              aria-label="Back"
            >
              <ArrowLeftIcon className="h-4 w-4" />
            </Button>
            <h3 className="font-medium">Create New Task</h3>
          </div>
        )}

        <div className="space-y-6 p-1">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                What are you working on? <span className="text-red-500">*</span>
              </label>
              <Input
                value={newTask.title}
                onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleSubmit(e as any)
                  }
                }}
                placeholder="Enter task title..."
                className="w-full"
              />
            </div>

            <div className="space-y-3">
              <span className="text-sm font-medium">
                Notes <span className="text-muted-foreground">(optional)</span>
              </span>
              <Input
                placeholder="Notes"
                value={newTask.notes}
                onChange={(e) => setNewTask(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>

            <div className="space-y-3">
              <span className="text-sm font-medium">
                Estimated Pomodoros <span className="text-primary/90">(required)</span>
              </span>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="99"
                  value={newTask.estimatedPomodoros}
                  onChange={(e) => setNewTask(prev => ({ 
                    ...prev, 
                    estimatedPomodoros: Math.max(1, Math.min(99, parseInt(e.target.value) || 1))
                  }))}
                  className="w-24"
                  required
                />
                <span className="text-sm text-muted-foreground">
                  {newTask.estimatedPomodoros === 1 ? 'pomodoro' : 'pomodoros'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Estimate how many pomodoro sessions this task will take
              </p>
            </div>

            <div className="space-y-3">
              <span className="text-sm font-medium">
                Project <span className="text-muted-foreground">(optional)</span>
              </span>
              <div className="flex gap-2">
                <div className="flex-1">
                  <ProjectSelector
                    value={newTask.projectId}
                    onChange={(project) => setNewTask(prev => ({ 
                      ...prev, 
                      projectId: project?.id || null 
                    }))}
                    projects={projects}
                  />
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowProjectForm(true)}
                      >
                        <PlusIcon className="h-4 w-4" />
                        Create New Project
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Create New Project</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>

          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">Advanced Options</h3>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                className="gap-1"
              >
                Advanced Options
                {showAdvancedOptions ? (
                  <ChevronUpIcon className="h-4 w-4" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4" />
                )}
              </Button>
            </div>

            {showAdvancedOptions && (
              <div className="space-y-8 animate-slideIn mb-16">
                <div className="space-y-2 mt-4">
                  <label className="block text-sm font-medium">
                    Priority <span className="text-muted-foreground">(optional)</span>
                  </label>
                  <PrioritySelect
                    value={newTask.priority}
                    onChange={(priority) => setNewTask(prev => ({ ...prev, priority }))}
                  />
                </div>

                <div className="space-y-2 mt-8">
                  <label className="block text-sm font-medium">
                    Tags <span className="text-muted-foreground">(optional)</span>
                  </label>
                  <TagInput
                    value={newTask.tags}
                    onChange={(tags) => setNewTask(prev => ({ ...prev, tags }))}
                    placeholder="Add tags"
                  />
                  <div className="text-sm text-muted-foreground space-y-2">
                    <p className="text-sm">Organize and quickly find related tasks with tags</p>
                    
                    <div className="space-y-1.5">
                      <p className="text-xs uppercase tracking-wider opacity-70">Popular tags:</p>
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-500/10 text-blue-500 text-sm">#work</span>
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-green-500/10 text-green-500 text-sm">#study</span>
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-purple-500/10 text-purple-500 text-sm">#personal</span>
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-orange-500/10 text-orange-500 text-sm">#project</span>
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-pink-500/10 text-pink-500 text-sm">#health</span>
                      </div>
                    </div>
                    
                    <p className="text-xs opacity-70 pt-1">
                      Tip: Press Enter or use comma (,) to add multiple tags
                    </p>
                  </div>
                </div>

                <div className="space-y-2 mt-8 mb-20">
                  <label className="block text-sm font-medium">
                    Recurring <span className="text-muted-foreground">(optional)</span>
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: 'daily', color: 'rose' },
                      { value: 'weekly', color: 'emerald' },
                      { value: 'monthly', color: 'amber' }
                    ].map(({ value, color }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setNewTask(prev => ({
                          ...prev,
                          recurring: prev.recurring === value ? undefined : value
                        }))}
                        className={cn(
                          "px-3 py-2 rounded-lg border-2 text-sm capitalize transition-all duration-200",
                          newTask.recurring === value
                            ? color === 'rose' 
                              ? "border-rose-500 bg-rose-50/80 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-800"
                              : color === 'emerald'
                              ? "border-emerald-500 bg-emerald-50/80 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800"
                              : "border-amber-500 bg-amber-50/80 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800"
                            : "border-border hover:border-primary/30 hover:bg-muted/50"
                        )}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Task will automatically repeat on the selected interval
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 mt-8">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button type="submit" className="flex-1">
            Create Task
          </Button>
        </div>
      </form>

      <ProjectForm
        isOpen={showProjectForm}
        onClose={() => setShowProjectForm(false)}
      />
    </>
  );
};